import { ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useNavigate } from 'react-router-dom';
import Header from '../../common/Header/Header';
import {
  Buttons,
  Page,
  TableWrapper,
} from '../../common/TableWithButtonsRightLayout/TableWithButtonsRightLayout.styles';
import Button from '../../components/Button/Button';
import { Variant } from '../../components/Button/Button.types';
import Table from '../../components/Table/Table';
import { useOptions } from '../../services/option/option.service';
import { Option } from '../../services/option/option.types';
import { useFetchErrorToast } from '../../utils/useFetchErrorToasts/useFetchErrorToast';
import Divider from '@mui/material/Divider';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const Options = () => {
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact>(null);
  const [selectedRow, setSelectedRow] = useState<Option | undefined>(undefined);
  const { data, isError: optionsError } = useOptions(true);
  const muiTheme = createTheme();


  const allOptions = data?.data || [];

  const headers = [
    { label: 'MOA Class No.', key: 'classNo' },
    { label: 'MOA Option No.', key: 'optionNo' },
    { label: 'Description', key: 'description' },
    { label: 'Status No.', key: 'statusNo' },
    { label: 'Make Sequence', key: 'makeSeq' },
    { label: 'Top TP Code', key: 'toppingTypeCode' },
    { label: 'Top Sub No.', key: 'toppingSubstitutionNo' },
    { label: 'Exclude Option No.', key: 'excludeOptionNo' },
    { label: 'Mnemonic', key: 'mnemonic' },
    { label: 'Sequence No.', key: 'sequenceNo' },
    { label: 'Long Description', key: 'longDescription' },
    { label: 'Web Display Seq. No.', key: 'webDisplay' },
    { label: 'Valid on Web', key: 'validOnWeb' },
    { label: 'Image ID', key: 'imageId' },
    { label: 'Display in Order Entry', key: 'displayInOrderEntry' },
    { label: 'BOH Description', key: 'displayDesc' },
    { label: 'Topping Category', key: 'toppingCategory' },
    { label: 'BOH Modifier', key: 'isModifier' },
    { label: 'BOH Process', key: 'opsProcess' },
    { label: 'Pricing Sequence', key: 'pricingSeq' },
    { label: 'Created By', key: 'createdBy' },
    { label: 'Created Date', key: 'createdDate' },
    { label: 'Updated By', key: 'updatedBy' },
    { label: 'Updated Date', key: 'updatedDate' },
  ];

  const csvData = allOptions.map((option: Option) => ({
    classNo: option.classNo,
    optionNo: option.optionNo,
    description: option.description,
    statusNo: option.statusNo,
    makeSeq: option.makeSeq,
    toppingTypeCode: option.toppingTypeCode,
    toppingSubstitutionNo: option.toppingSubstitutionNo,
    excludeOptionNo: option.excludeOptionNo,
    mnemonic: option.mnemonic,
    sequenceNo: option.sequenceNo,
    longDescription: option.longDescription,
    webDisplay: option.webDisplay,
    validOnWeb: option.validOnWeb ? 'Y' : 'N',
    imageId: option.imageId,
    displayInOrderEntry: option.displayInOrderEntry ? 'Y' : 'N',
    displayDesc: option.displayDesc,
    toppingCategory: option.toppingCategory,
    isModifier: option.isModifier ? 'Y' : 'N',
    opsProcess: option.opsProcess,
    pricingSeq: option.pricingSeq,
    createdBy: option.createdBy,
    createdDate: option.createdDate,
    updatedBy: option.updatedBy,
    updatedDate: option.updatedDate,
  }));

  const handleRowSelection = () => {
    const selectedRows = gridRef?.current?.api.getSelectedRows();
    setSelectedRow(selectedRows?.[0]);
  };

  const handleOpenClick = () => {
    navigate(`/tables/options/${selectedRow?.classNo}/${selectedRow?.optionNo}`);
  };

  useFetchErrorToast([{ isError: optionsError, label: 'Error fetching options', id: 'optionsFetchError' }]);

  return (
    <>
      <Header breadcrumb={{ to: '/tables', label: 'All tables' }} title='Option' />
      <Page>
        <TableWrapper>
          <Table
            ref={gridRef}
            rowData={allOptions}
            returnedRecords={allOptions?.length || 0}
            height={'100%'}
            rowSelection='single'
            onRowSelected={handleRowSelection}
            columnDefs={[
              {
          minWidth: 48,
          field: '',
          headerName: '',
          checkboxSelection: true,
          floatingFilter: false,
          sortable: false,
              },
              { minWidth: 120, field: 'classNo', headerName: 'MOA Class No.' },
              { minWidth: 120, field: 'optionNo', headerName: 'MOA Option No.' },
              { minWidth: 120, field: 'optionNo', headerName: 'Option No.' },
              { minWidth: 120, field: 'description', headerName: 'Description' },
              { minWidth: 120, field: 'statusNo', headerName: 'Status No.' },
              { minWidth: 120, field: 'makeSeq', headerName: 'Make Sequence' },
              { minWidth: 120, field: 'toppingTypeCode', headerName: 'Top TP Code' },
              { minWidth: 120, field: 'toppingSubstitutionNo', headerName: 'Top Sub No.' },
              { minWidth: 120, field: 'excludeOptionNo', headerName: 'Exclude Option No.' },
              { minWidth: 120, field: 'mnemonic', headerName: 'Mnemonic' },
              { minWidth: 120, field: 'sequenceNo', headerName: 'Sequence No.' },
              { minWidth: 120, field: 'longDescription', headerName: 'Long Description' },
              { minWidth: 120, field: 'webDisplay', headerName: 'Web Display Seq. No.' },
              {
          minWidth: 120,
          field: 'validOnWeb',
          headerName: 'Valid on Web',
          valueFormatter: (params) => (params.value === true ? 'Y' : 'N'),
          filterValueGetter: (params: ValueGetterParams) => (params.getValue('validOnWeb') === true ? 'Y' : 'N'),
              },
              {
          minWidth: 120,
          field: 'displayInOrderEntry',
          headerName: 'Display in Order Entry',
          valueFormatter: (params) => (params.value === true ? 'Y' : 'N'),
          filterValueGetter: (params: ValueGetterParams) =>
            params.getValue('displayInOrderEntry') === true ? 'Y' : 'N',
              },
              { minWidth: 120, field: 'displayDesc', headerName: 'BOH Description' },
              { minWidth: 120, field: 'opsProcess', headerName: 'BOH Process' },
              {
          minWidth: 120,
          field: 'isModifier',
          headerName: 'BOH Modifier',
          valueFormatter: (params) => (params.value === true ? 'Y' : 'N'),
          filterValueGetter: (params: ValueGetterParams) => (params.getValue('isModifier') === true ? 'Y' : 'N'),
              },
              { minWidth: 120, field: 'toppingCategory', headerName: 'Topping Category' },
              { minWidth: 120, flex: 1, field: 'pricingSeq', headerName: 'Pricing Sequence', resizable: false },
              { 
          minWidth: 80, 
          field: 'imageId', 
          headerName: 'GUI Image',
          valueFormatter: (params) => (params.value ? 'Y' : 'N'),
          filterValueGetter: (params: ValueGetterParams) => (params.getValue('imageId') ? 'Yes' : 'No'),
              },
            ]}
          />
        </TableWrapper>
        <Buttons>
          <Button
            label='Open'
            variant={Variant.Primary}
            onClick={handleOpenClick}
            disabled={selectedRow === undefined}
          />
          <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
            <Divider orientation='horizontal' variant={'fullWidth'} flexItem />
          </ThemeProvider>
          <CSVLink
            data={csvData}
            headers={headers}
            filename={`options_${new Date().toISOString().split('T')[0]}.csv`}
            target='_blank'
            className='btn btn-primary'
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <Button label='Export to CSV' variant={Variant.Secondary} />
          </CSVLink>
        </Buttons>
      </Page>
    </>
  );
};

export default Options;
